import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	CustomerClassificationInput,
	CustomerClassificationListParams,
} from '@/types/customer-classification'
import {
	createCustomerClassification,
	deleteCustomerClassification,
	getCustomerClassifications,
	updateCustomerClassification,
} from '../services/customer-classifications'

export const customerClassificationKeys = {
	all: ['customer-classifications'] as const,
	list: (params?: CustomerClassificationListParams) =>
		[...customerClassificationKeys.all, 'list', params] as const,
}

export function useCustomerClassifications(
	params?: CustomerClassificationListParams,
	options?: { enabled?: boolean },
) {
	return useQuery({
		queryKey: customerClassificationKeys.list(params),
		queryFn: () => getCustomerClassifications(params),
		staleTime: 2 * 60 * 1000,
		enabled: options?.enabled ?? true,
	})
}

export function useCreateCustomerClassification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CustomerClassificationInput) =>
			createCustomerClassification(body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: customerClassificationKeys.all,
			})
		},
	})
}

export function useUpdateCustomerClassification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: CustomerClassificationInput
		}) => updateCustomerClassification(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: customerClassificationKeys.all,
			})
		},
	})
}

export function useDeleteCustomerClassification() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteCustomerClassification(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: customerClassificationKeys.all,
			})
		},
	})
}
